import { ObjectCategory, ZIndexes } from "@common/constants";
import { type DecalDefinition, goreDrawn } from "@common/definitions/decals";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { GameConsole } from "../console/gameConsole";
import { BloodTrailMarks } from "../managers/bloodTrails";
import { Game } from "../game";
import { DIFF_LAYER_HITBOX_OPACITY, HITBOX_COLORS } from "../utils/constants";
import { DebugRenderer } from "../utils/debugRenderer";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { GameObject } from "./gameObject";

/**
 * the battlefield's gore lying about the map (0.86: server/src/data/mapGore.ts), drawn by the player's gore setting as
 * the runtime gore is: blood unless it's off, remains (body parts, bones) on "extreme" only. Kept here, so a change of
 * the setting shows or hides them at once
 */
const goreDecals = new Set<Decal>();
let goreListening = false;
function goreShown(kind: "blood" | "remains"): boolean {
    let level: string;
    try {
        level = GameConsole.getBuiltInCVar("cv_gore");
    } catch {
        level = "extreme";
    }
    return goreDrawn(kind, level);
}

export class Decal extends GameObject.derive(ObjectCategory.Decal) {
    definition!: DecalDefinition;

    readonly image: TrenchioSprite;

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Decal]) {
        super(id);

        this.image = new TrenchioSprite();

        this.updateFromData(data);
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Decal]): void {
        this.position = data.position;

        this.layer = data.layer;
        this.updateLayer();

        const definition = this.definition = data.definition;

        this.image.setFrame(definition.image ?? definition.idString);

        if (this.definition.alpha !== undefined) {
            this.image.setAlpha(this.definition.alpha);
        }

        this.container.addChild(this.image);
        this.container.scale.set(definition.scale ?? 1);

        this.container.position.copyFrom(toPixiCoords(this.position));
        this.container.rotation = data.rotation;

        this.container.zIndex = this.definition.zIndex ?? ZIndexes.Decals;

        // a blood trail's mark (1.06): tinted as it dries, by its age (managers/bloodTrails.ts)
        if (definition.trail) BloodTrailMarks.add(this, definition.trail, data.trail?.age ?? 0, data.trail?.strength ?? 1);

        if (definition.gore) {
            goreDecals.add(this);
            if (!goreListening) {
                goreListening = true;
                GameConsole.variables.addChangeListener("cv_gore", () => {
                    for (const decal of goreDecals) decal.showGore();
                });
            }
            this.showGore();
        }
    }

    /** a gore decal shown or hidden by the gore setting */
    showGore(): void {
        const kind = this.definition.gore;
        if (kind) this.container.visible = goreShown(kind);
    }

    update(): void { /* bleh */ }
    updateInterpolation(): void { /* bleh */ }
    updateDebugGraphics(): void {
        if (!DEBUG_CLIENT) return;

        DebugRenderer.addCircle(0.1 * (this.definition.scale ?? 1),
            this.position,
            HITBOX_COLORS.obstacleNoCollision,
            this.layer === Game.layer ? 1 : DIFF_LAYER_HITBOX_OPACITY
        );
    }

    override destroy(): void {
        goreDecals.delete(this);
        BloodTrailMarks.remove(this);
        this.image.destroy();
        // (its container too: every decal that went out of view left an empty one behind in the world until the match
        // ended, and the map's gore goes in and out of view all the time: the review of 0.86)
        super.destroy();
    }
}
